/**
 * Generate a strip of thumbnail data URLs by seeking a <video> element to
 * evenly spaced timestamps and drawing frames to a canvas.
 *
 * Si un paso (la metadata o un seek) no llega en `ESPERA_POR_PASO_MS`, devuelve las
 * miniaturas que alcanzó a sacar: antes esperaba para siempre y, como el editor las
 * arma de a dos, dos videos trabados frenaban todas las tiras
 * (ADR-2026-11-03-el-play-sigue-al-video).
 */
export const ESPERA_POR_PASO_MS = 8000;

export async function generateVideoThumbnails(
  src: string,
  count = 10,
  thumbWidth = 80,
): Promise<string[]> {
  return new Promise(resolve => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    // Cada seek pide sólo el pedazo que necesita: con 'auto' el elemento oculto
    // bajaba el archivo entero otra vez (ADR-2026-11-03-bajar-solo-lo-que-se-ve).
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = src;

    const thumbs: string[] = [];
    const canvas = document.createElement('canvas');
    let times: number[] = [];
    let idx = 0;
    let terminado = false;
    let vigia: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      clearTimeout(vigia);
      video.removeAttribute('src');
      video.load();
    };

    const terminar = () => {
      if (terminado) return;
      terminado = true;
      cleanup();
      resolve(thumbs);
    };
    const onError = terminar;
    const vigilar = () => {
      clearTimeout(vigia);
      vigia = setTimeout(terminar, ESPERA_POR_PASO_MS);
    };
    vigilar();

    video.addEventListener('loadedmetadata', () => {
      const dur = video.duration;
      if (!Number.isFinite(dur) || dur <= 0) {
        onError();
        return;
      }
      const aspect = video.videoWidth > 0 ? video.videoHeight / video.videoWidth : 9 / 16;
      canvas.width = thumbWidth;
      canvas.height = Math.max(1, Math.round(thumbWidth * aspect));
      times = Array.from({ length: count }, (_, i) =>
        count === 1 ? dur / 2 : (dur * i) / (count - 1),
      );
      vigilar();
      video.currentTime = times[0];
    });

    video.addEventListener('seeked', () => {
      if (terminado) return;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          thumbs.push(canvas.toDataURL('image/jpeg', 0.55));
        } catch {
          // Canvas tainted — abort
          terminar();
          return;
        }
      }
      idx++;
      if (idx < times.length) {
        vigilar();
        video.currentTime = times[idx];
      } else {
        terminar();
      }
    });

    video.addEventListener('error', onError);
  });
}
